
import React, { useState, useEffect, useRef } from 'react';
import { generateStoryContent, generateImageForSegment, generateAudioForText } from './services/geminiService';
import { Story, InputLanguage, User } from './types';
import LandingPage from './components/LandingPage';
import AuthForm from './components/AuthForm';
import ProfileView from './components/ProfileView';
import StoryViewer from './components/StoryViewer';
import LibraryView from './components/LibraryView';
import ExploreView from './components/ExploreView';
import Button from './components/Button';
import { translations, UILanguage } from './translations';
import { 
  saveStoryToDB, 
  getStoriesByUserPreview, 
  getAllStoriesPreview,
  deleteStoryFromDB,
  getUserFromDB,
  getStoryById
} from './services/dbService';

const SESSION_KEY = 'amadal_user_id';
const LANG_KEY = 'amadal_ui_lang';

const LANG_INFO: Record<UILanguage, { label: string; flag: string }> = {
  fr: { label: 'Français', flag: '🇫🇷' },
  ar: { label: 'العربية', flag: '🇲🇦' },
  en: { label: 'English', flag: '🇬🇧' },
  ber: { label: 'Tamazight', flag: 'ⵣ' },
  es: { label: 'Español', flag: '🇪🇸' }
};

const App: React.FC = () => {
  const [uiLang, setUiLang] = useState<UILanguage>((localStorage.getItem(LANG_KEY) as UILanguage) || 'fr');
  const [view, setView] = useState<'landing' | 'home' | 'viewer' | 'library' | 'auth' | 'explore'>('landing');
  const [user, setUser] = useState<User | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [pageCount, setPageCount] = useState(4);
  const [inputLang, setInputLang] = useState<InputLanguage>(InputLanguage.FRENCH);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingFullStory, setIsLoadingFullStory] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [currentStory, setCurrentStory] = useState<Story | null>(null);
  const [savedStories, setSavedStories] = useState<any[]>([]);
  const [allCommunityStories, setAllCommunityStories] = useState<any[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isReadOnlyView, setIsReadOnlyView] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);

  const t = translations[uiLang] || translations.fr;

  useEffect(() => {
    localStorage.setItem(LANG_KEY, uiLang);
    document.documentElement.dir = uiLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = uiLang;
  }, [uiLang]);

  // PROTECTION CRITIQUE : Ne jamais écraser une image existante par null
  const mergeStories = (oldList: any[], newList: any[]) => {
    return newList.map(newStory => {
      const existing = oldList.find(o => o.id === newStory.id);
      return {
        ...newStory,
        // Si la nouvelle version n'a pas d'image mais que l'ancienne en avait une, on garde l'ancienne
        coverImage: newStory.coverImage || existing?.coverImage || null
      };
    });
  };

  const loadData = async () => {
    try {
      const community = await getAllStoriesPreview(12);
      setAllCommunityStories(prev => mergeStories(prev, community));

      const savedUserId = localStorage.getItem(SESSION_KEY);
      if (savedUserId) {
        const u = await getUserFromDB(savedUserId);
        if (u) {
          setUser(u);
          const userPreviews = await getStoriesByUserPreview(u.id);
          setSavedStories(prev => mergeStories(prev, userPreviews));
        }
      }
    } catch (e) {
      console.error("Data loading failed", e);
    }
  };

  useEffect(() => {
    loadData();
    // Rafraîchissement périodique léger pour récupérer les images qui ont fini de charger en arrière-plan
    const interval = setInterval(async () => {
      const community = await getAllStoriesPreview(12);
      setAllCommunityStories(prev => mergeStories(prev, community));
      if (user) {
        const userPreviews = await getStoriesByUserPreview(user.id);
        setSavedStories(prev => mergeStories(prev, userPreviews));
      }
    }, 8000); 
    return () => clearInterval(interval);
  }, [user?.id]);

  const handleSelectStory = async (storyId: string, readOnly: boolean = false) => {
    setIsLoadingFullStory(true);
    try {
      const fullStory = await getStoryById(storyId);
      if (fullStory) {
        setCurrentStory(fullStory);
        setIsReadOnlyView(readOnly);
        setView('viewer');
      }
    } catch (err) {
      showToast("Erreur lors du chargement du conte.");
    } finally {
      setIsLoadingFullStory(false);
    }
  };

  const handleAuthSuccess = (u: User) => {
    setUser(u);
    localStorage.setItem(SESSION_KEY, u.id);
    setView('home');
    getStoriesByUserPreview(u.id).then(stories => setSavedStories(prev => mergeStories(prev, stories)));
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setUser(null);
    setSavedStories([]);
    setView('landing');
    setIsProfileOpen(false);
  };

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleGenerate = async () => {
    if (!prompt.trim() || !user) {
        if (!user) setView('auth');
        return;
    };
    setIsGenerating(true);
    setIsReadOnlyView(false);
    try {
      setGenerationStep(t.thinking);
      const generatedContent = await generateStoryContent(prompt, inputLang, pageCount);
      
      const enhancedSegments = [];
      for (let i = 0; i < generatedContent.segments.length; i++) {
        const segment = generatedContent.segments[i];
        setGenerationStep(`${t.painting} (${i + 1}/${pageCount})`);
        const imageUrl = await generateImageForSegment(segment.imagePrompt);
        
        setGenerationStep(`Enchantement de la voix... (${i + 1}/${pageCount})`);
        let audioData = '';
        try {
          audioData = await generateAudioForText(segment.text);
        } catch (audioError) {
          console.warn("Audio skipped for segment", i);
        }

        enhancedSegments.push({ ...segment, imageUrl, audioData });
        if (i < generatedContent.segments.length - 1) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      const newStory: Story = {
        id: Date.now().toString(),
        userId: user.id,
        title: generatedContent.title,
        segments: enhancedSegments,
        createdAt: Date.now()
      };

      setCurrentStory(newStory);
      setView('viewer');
      await saveStoryToDB(newStory);
      
      loadData();
    } catch (error: any) { 
      console.error("Generation error:", error);
      // Toujours afficher le message générique demandé par l'utilisateur pour la génération
      alert(t.quotaError);
    }
    finally { setIsGenerating(false); setGenerationStep(''); }
  };

  const handleSaveStory = async (story: Story) => {
    await saveStoryToDB(story);
    const updatedPreviews = await getStoriesByUserPreview(user!.id);
    setSavedStories(prev => mergeStories(prev, updatedPreviews));
    showToast("Histoire sauvegardée !");
  };

  const handleUpdateStory = async (story: Story) => {
    await saveStoryToDB(story);
    setCurrentStory(story);
    const updatedPreviews = await getStoriesByUserPreview(user!.id);
    setSavedStories(prev => mergeStories(prev, updatedPreviews));
    showToast("Texte mis à jour !");
  };

  const handleDeleteStory = async (id: string) => {
    if (confirm("Supprimer cette histoire ?")) {
      await deleteStoryFromDB(id);
      setSavedStories(prev => prev.filter(s => s.id !== id));
      showToast("Histoire supprimée !");
    }
  };

  const resetToLanding = () => {
    setView('landing');
    setIsReadOnlyView(false);
    setIsProfileOpen(false);
    loadData();
  };

  const LanguageSelector = () => (
    <div className="relative" ref={langMenuRef}>
      <button 
        onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
        className="flex items-center gap-2 bg-indigo-50 hover:bg-white text-indigo-900 px-3 py-2 rounded-xl border border-indigo-100 transition-all shadow-sm"
      >
        <span className="text-xl">{LANG_INFO[uiLang].flag}</span>
        <span className="text-xs font-bold hidden sm:block">{LANG_INFO[uiLang].label}</span>
      </button>

      {isLangMenuOpen && (
        <div className="absolute top-full mt-2 right-0 w-40 bg-white rounded-2xl shadow-2xl border border-indigo-50 py-2 z-[100] animate-fadeIn">
          {(Object.keys(LANG_INFO) as UILanguage[]).map((lang) => (
            <button
              key={lang}
              onClick={() => {
                setUiLang(lang);
                setIsLangMenuOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-bold ${uiLang === lang ? 'bg-indigo-600 text-white' : 'text-indigo-900 hover:bg-indigo-50'}`}
            >
              <span>{LANG_INFO[lang].flag}</span>
              <span>{LANG_INFO[lang].label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  if (view === 'landing') return (
    <LandingPage 
      t={t} uiLang={uiLang} userExists={!!user}
      onStart={() => user ? setView('home') : setView('auth')} 
      onViewStory={(s) => handleSelectStory(s.id, true)}
      onGoToLibrary={() => setView('explore')} 
      onLogoClick={resetToLanding}
      langSelector={<LanguageSelector />} 
      communityStories={allCommunityStories}
    />
  );
  
  if (view === 'auth') return <AuthForm t={t} uiLang={uiLang} onSuccess={handleAuthSuccess} onBack={resetToLanding} />;

  return (
    <div className="min-h-screen flex flex-col font-kids">
      {isLoadingFullStory && (
        <div className="fixed inset-0 bg-white/60 backdrop-blur-md z-[200] flex items-center justify-center flex-col gap-4">
           <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
           <p className="text-indigo-900 font-black animate-pulse uppercase tracking-widest text-xs">Ouverture du grimoire...</p>
        </div>
      )}

      {toast && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] bg-indigo-900 text-white px-6 py-3 rounded-2xl shadow-2xl animate-fadeIn">
          {toast}
        </div>
      )}

      {isProfileOpen && user && (
        <ProfileView 
          t={t} user={user} onUpdate={setUser} 
          onLogout={handleLogout} onClose={() => setIsProfileOpen(false)} 
        />
      )}

      <header className="py-6 px-6 md:px-12 bg-white/70 backdrop-blur-md border-b border-indigo-50 sticky top-0 z-10 flex items-center justify-between no-print">
        <button onClick={resetToLanding} className="flex items-center gap-3 group">
          <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">ⵣ</div>
          <h1 className="text-xl font-bold text-indigo-700 hidden lg:block font-tifinagh tracking-widest uppercase">ⵜⵉⵏⴼⵓⵙⵉⵏ ⵏ ⵉⵎⵥⵥⵢⴰⵏⵏ</h1>
        </button>

        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSelector />
          <Button variant="secondary" onClick={() => setView('library')} className="!py-2 !px-4 !text-xs">
            {t.library}
          </Button>
          <button 
            onClick={() => setIsProfileOpen(true)}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shadow-md hover:scale-105 transition-transform"
            style={{ backgroundColor: user?.avatarColor }}
          >
            {user?.username.charAt(0).toUpperCase()}
          </button>
        </div>
      </header>

      <main className="flex-1 py-8">
        {view === 'home' && (
          <div className="max-w-2xl mx-auto w-full px-6 space-y-8 animate-slideUp">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-bold text-indigo-950">{t.newStory}</h2>
              <p className="text-indigo-400">{t.newStoryDesc}</p>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] shadow-xl border border-indigo-50 space-y-6">
              <div className="space-y-4">
                <label className="text-xs font-bold text-indigo-900 uppercase mx-2">{t.lang}</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.values(InputLanguage).map((lang) => (
                    <button key={lang} onClick={() => setInputLang(lang)} className={`py-2 rounded-xl text-xs font-bold transition-all ${inputLang === lang ? 'bg-indigo-600 text-white shadow-lg' : 'bg-indigo-50 text-indigo-400'}`}>
                      {lang}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center mx-2">
                  <label className="text-xs font-bold text-indigo-900 uppercase">{t.pages}</label>
                  <span className="text-indigo-600 font-bold">{pageCount}</span>
                </div>
                <input type="range" min="2" max="6" value={pageCount} onChange={(e) => setPageCount(parseInt(e.target.value))} className="w-full accent-indigo-600" />
              </div>

              <textarea 
                value={prompt} 
                onChange={(e) => setPrompt(e.target.value)} 
                placeholder={t.storyPromptPlaceholder} 
                className="w-full h-32 p-5 rounded-3xl bg-indigo-50 border-transparent focus:bg-white focus:border-indigo-100 outline-none transition-all resize-none text-indigo-900" 
              />

              <Button onClick={handleGenerate} className="w-full py-5 text-xl rounded-2xl shadow-indigo-100" isLoading={isGenerating}>
                {t.genBtn}
              </Button>
              {isGenerating && (
                <div className="space-y-3">
                  <p className="text-center text-indigo-500 font-bold animate-pulse">{generationStep}</p>
                  <div className="w-full h-1.5 bg-indigo-50 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 animate-[loading_8s_linear_infinite]"></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'viewer' && currentStory && (
          <StoryViewer 
            story={currentStory} 
            readOnly={isReadOnlyView}
            canEdit={user?.id === currentStory.userId}
            onReset={resetToLanding} 
            onSave={handleSaveStory}
            onUpdate={handleUpdateStory}
            isSaved={savedStories.some(s => s.id === currentStory.id)}
            t={t}
          />
        )}

        {view === 'library' && (
          <LibraryView 
            stories={savedStories} 
            onSelect={(s) => handleSelectStory(s, false)} 
            onDelete={handleDeleteStory} 
            onClearAll={() => {}} 
            onBack={() => setView('home')} 
          />
        )}

        {view === 'explore' && (
          <ExploreView 
            t={t}
            stories={allCommunityStories} 
            onSelect={(s) => handleSelectStory(s, true)}
            onBack={resetToLanding}
          />
        )}
      </main>
      <style>{`
        @keyframes loading {
          0% { width: 0%; }
          100% { width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default App;
